import type { GuestQuoteLinkParams, GuestQuoteView } from '@repo/api-contracts';
import { type ReactElement, useId } from 'react';
import { GuestQuoteLinkNotice, quoteImageAlt } from '@/entities/quote';
import { useIdentityAvailabilityQuery } from '@/entities/user';
import { GuestQuoteOrderButton } from '@/features/guest-quote-claim';
import { PrintQuoteButton } from '@/features/quote-response';
import { DesignThumbnail, EyebrowLabel, QuoteDocument } from '@/shared/ui';
import { GuestQuoteSummary } from './guest-quote-summary';
import { GuestQuoteSupport } from './guest-quote-support';

export interface GuestQuoteIssuedProps {
  readonly view: Extract<GuestQuoteView, { state: 'ISSUED' }>;
  readonly params: GuestQuoteLinkParams;
}

/**
 * 발행됨(결정 G9) — 발행 견적서·인쇄·[주문하기]. 견적서에는 배송지 표가 없다(링크 유출 대비 — 요약 계약에도 배송지가 없다).
 * 금액은 상품·배송비로 나뉘고, 배송비 줄 이름에는 배송지 번호·지역만 있다(교차 조정 R2).
 * 견적서만 인쇄되고 나머지는 print:hidden 이다. 수정 요청·거절·취소는 링크에서 하지 않고 고객센터로 안내한다.
 */
export const GuestQuoteIssued = ({ view, params }: GuestQuoteIssuedProps): ReactElement => {
  const documentHeadingId = useId();
  // 본인인증 단계는 서버가 쓴다고 확인됐을 때만 안내한다(불러오는 중·실패면 이메일 인증까지만).
  const identityRequired = useIdentityAvailabilityQuery().data?.identityRequired === true;

  return (
    <div className="flex flex-col gap-20 sm:gap-24">
      <div className="flex flex-col gap-10 print:hidden">
        <header className="flex flex-col gap-4 border-b border-foreground/10 pb-8">
          <EyebrowLabel accent>Quote</EyebrowLabel>
          <h1 className="text-[clamp(2rem,4vw,3.25rem)] leading-tight font-bold tracking-[-0.03em]">
            견적이 도착했어요
          </h1>
          <p className="max-w-2xl text-[15px] leading-relaxed text-muted-foreground">
            {identityRequired
              ? '아래 견적서를 확인하고 주문해 주세요. 주문할 때 회원가입과 이메일 인증·휴대폰 본인인증을 거쳐 이 견적을 계정에 연결해요.'
              : '아래 견적서를 확인하고 주문해 주세요. 주문할 때 회원가입과 이메일 인증을 거쳐 이 견적을 계정에 연결해요.'}
          </p>
        </header>

        <div className="grid gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] xl:gap-16">
          <DesignThumbnail
            url={view.thumbnailUrl ?? view.productImageUrl}
            alt={quoteImageAlt(view, view.productName, `${view.productName} 디자인 미리보기`)}
          />
          <div className="flex flex-col gap-8">
            <GuestQuoteSummary summary={view} />
            <div className="flex flex-col gap-3">
              <GuestQuoteOrderButton params={params} className="h-14 w-full text-base" />
              <PrintQuoteButton className="h-12 w-full" />
              <GuestQuoteLinkNotice />
              <p className="text-xs leading-relaxed text-muted-foreground">
                견적 수정·취소는 고객센터로 문의해 주세요.
              </p>
            </div>
          </div>
        </div>
      </div>

      <section aria-labelledby={documentHeadingId} className="flex flex-col gap-8">
        <h2
          id={documentHeadingId}
          className="border-b border-foreground pb-4 text-2xl leading-tight font-bold tracking-[-0.02em] print:hidden"
        >
          발행된 견적서
        </h2>
        <QuoteDocument
          version={view.issued}
          quoteNumber={view.quoteNumber}
          requestedAt={view.requestedAt}
          className="rounded-none border-foreground/15 sm:p-10"
        />
      </section>

      <div className="print:hidden">
        <GuestQuoteSupport />
      </div>
    </div>
  );
};
